import { useEffect, useRef, useState } from 'react'
import './Game.css'
import { createPlayer } from '@/online/client'
import Leaderboard from '@/online/Leaderboard'
import NicknameForm from '@/online/NicknameForm'
import { flushOutbox } from '@/online/sync'
import { type BonusKind, isNegative } from './bonuses'
import { features, gameConfig } from './config'
import { createFeedback } from './feedback'
import { IconShield, IconSoundOff, IconSoundOn, IconTrophy, IconWallet } from './icons'
import Logo from './Logo'
import { createGameLoop, type GameLoop } from './loop'
import { defaultSkin, isUnlocked, newlyUnlocked, resolveSkin, type Skin, skins } from './skins'
import type { GameEvent, Phase } from './state'
import {
  addCoins,
  getBestScore,
  getCoins,
  getPlayer,
  getSkinId,
  isMuted,
  type PlayerIdentity,
  recordGame,
  setMuted,
  setPlayer,
  setSkinId,
} from './storage'

/**
 * После game over кнопка «Ещё раз» появляется не сразу: палец в этот момент
 * ещё ведёт ракетку, и мгновенный рестарт случайным тапом бесит.
 */
const RESTART_DELAY_MS = 600
/** С какой длины серии показывать её под счётом: 1–2 точных удара — случайность. */
const COMBO_BADGE_FROM = 3

/** Бонусы с плашкой. Монета и «сбой серии» мгновенные — плашки у них нет. */
type EffectKind = Exclude<BonusKind, 'coin' | 'breaker'>

const EFFECT_LABEL: Record<EffectKind, string> = {
  wide: 'Широкая ракетка',
  slow: 'Замедление',
  double: '×2 очков',
  shield: 'Страховка',
  narrow: 'Узкая ракетка',
  fast: 'Ускорение',
  fog: 'Туман',
}

/**
 * Какие плашки снимает пойманный бонус — зеркало гашения в bonuses.applyBonus:
 * узкая ↔ широкая, ускорение ↔ замедление, сбой серии снимает «×2».
 */
const CANCELS: Partial<Record<BonusKind, EffectKind>> = {
  wide: 'narrow',
  narrow: 'wide',
  slow: 'fast',
  fast: 'slow',
  breaker: 'double',
}

/** Длительность эффекта для полоски обратного отсчёта; у страховки её нет. */
function effectSeconds(kind: EffectKind): number | null {
  const b = gameConfig.bonus
  const seconds: Record<EffectKind, number | null> = {
    wide: b.wideDuration,
    slow: b.slowDuration,
    double: b.doubleDuration,
    shield: null,
    narrow: b.narrowDuration,
    fast: b.fastDuration,
    fog: b.fogDuration,
  }
  return seconds[kind]
}

interface EffectChip {
  kind: EffectKind
  /** Номер активации — ключ, перезапускающий CSS-отсчёт при продлении. */
  key: number
  /** Момент истечения (Date.now() + длительность); null — без таймера (страховка). */
  expiresAt: number | null
}

/** Пока скины спрятаны, игра всегда рисует классику — даже если раньше выбрали другой. */
function initialSkin(): Skin {
  return features.skins ? resolveSkin(getSkinId(), getBestScore()) : defaultSkin
}

export default function Game() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const scoreRef = useRef<HTMLDivElement>(null)
  const loopRef = useRef<GameLoop | null>(null)
  const [feedback] = useState(() => createFeedback(isMuted()))
  const [phase, setPhase] = useState<Phase>('idle')
  const [score, setScore] = useState(0)
  const [combo, setCombo] = useState(0)
  const [points, setPoints] = useState(1)
  /** Номер точного удара — ключ, перезапускающий CSS-анимацию «Точно!». */
  const [perfectKey, setPerfectKey] = useState(0)
  const [best, setBest] = useState(getBestScore)
  const [isNewBest, setIsNewBest] = useState(false)
  const [unlocked, setUnlocked] = useState<Skin[]>([])
  const [canRestart, setCanRestart] = useState(true)
  const [skin, setSkin] = useState(initialSkin)
  const [chips, setChips] = useState<EffectChip[]>([])
  /** Тик для текста «— N с» на бейджах. Секундная частота — не кадровая, дешёво. */
  const [now, setNow] = useState(() => Date.now())
  /** Монеты этой партии и кошелёк целиком. */
  const [gameCoins, setGameCoins] = useState(0)
  const [wallet, setWallet] = useState(getCoins)
  const [muted, setMutedState] = useState(isMuted)
  const [player, setPlayerState] = useState<PlayerIdentity | null>(getPlayer)
  const [showBoard, setShowBoard] = useState(false)
  // Скин на момент монтирования: цикл создаётся один раз, дальше — через setSkin.
  const initialSkinRef = useRef(skin)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    let restartTimer: ReturnType<typeof setTimeout> | undefined
    const chipTimers = new Set<ReturnType<typeof setTimeout>>()
    let chipCounter = 0

    const onEvent = (event: GameEvent) => {
      switch (event.type) {
        case 'hit':
          feedback.hit(event.perfect, event.combo)
          setScore(event.score)
          setCombo(event.combo)
          setPoints(event.points)
          if (event.perfect) setPerfectKey((k) => k + 1)
          return
        case 'wind':
          // Сам порыв рисуется на канве (render.ts) прямо на корте — отдельная
          // DOM-плашка в HUD не нужна, подпись «Ветер →» уже часть рисунка.
          return
        case 'bonus': {
          feedback.bonus(event.kind === 'coin' ? 'coin' : isNegative(event.kind) ? 'bad' : 'good')
          const cancelled = CANCELS[event.kind]
          if (cancelled) setChips((cs) => cs.filter((c) => c.kind !== cancelled))
          if (event.kind === 'coin') {
            setGameCoins((c) => c + 1)
            return
          }
          if (event.kind === 'breaker') {
            setCombo(0)
            return
          }
          const kind = event.kind
          const key = ++chipCounter
          const seconds = effectSeconds(kind)
          const expiresAt = seconds !== null ? Date.now() + seconds * 1000 : null
          setChips((cs) => [...cs.filter((c) => c.kind !== kind), { kind, key, expiresAt }])
          if (seconds !== null) {
            const timer = setTimeout(() => {
              chipTimers.delete(timer)
              setChips((cs) => cs.filter((c) => c.key !== key))
            }, seconds * 1000)
            chipTimers.add(timer)
          }
          return
        }
        case 'shield':
          feedback.shield()
          setCombo(0)
          setChips((cs) => cs.filter((c) => c.kind !== 'shield'))
          return
        case 'miss':
          feedback.miss()
          return
        case 'over': {
          const before = getBestScore()
          const result = recordGame({
            score: event.score,
            hits: event.hits,
            duration: event.duration,
          })
          void flushOutbox()
          setBest(result.best)
          setIsNewBest(result.isNewBest)
          setUnlocked(newlyUnlocked(before, result.best))
          setChips([])
          setWallet(addCoins(event.coins))
          setCanRestart(false)
          setPhase('over')
          restartTimer = setTimeout(() => setCanRestart(true), RESTART_DELAY_MS)
        }
      }
    }

    const loop = createGameLoop(canvas, onEvent, initialSkinRef.current.colors)
    loopRef.current = loop
    return () => {
      clearTimeout(restartTimer)
      for (const t of chipTimers) clearTimeout(t)
      loop.destroy()
      loopRef.current = null
    }
  }, [feedback])

  useEffect(() => {
    loopRef.current?.setSkin(skin.colors)
  }, [skin])

  // Поп табло — императивно: узел счёта один на всю партию, без перемонтирования.
  useEffect(() => {
    if (score === 0) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    scoreRef.current?.animate?.([{ transform: 'scale(1.22)' }, { transform: 'scale(1)' }], {
      duration: 180,
      easing: 'ease-out',
    })
  }, [score])

  // Текст «— N с» на бейджах баффов: тикает раз в четверть секунды, только
  // пока есть активный лимитированный эффект — простаивающий интервал не нужен.
  useEffect(() => {
    if (!chips.some((c) => c.expiresAt !== null)) return
    const id = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(id)
  }, [chips])

  // Партии, сыгранные без сети, уходят при первой возможности.
  useEffect(() => {
    void flushOutbox()
    const onOnline = () => void flushOutbox()
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [])

  const start = () => {
    feedback.unlock()
    loopRef.current?.start()
    setScore(0)
    setCombo(0)
    setPoints(1)
    setIsNewBest(false)
    setUnlocked([])
    setChips([])
    setGameCoins(0)
    setPhase('playing')
  }

  const chooseSkin = (next: Skin) => {
    setSkin(next)
    setSkinId(next.id)
  }

  const saveNickname = async (nickname: string) => {
    const created = await createPlayer(nickname)
    setPlayer(created)
    setPlayerState(created)
    await flushOutbox()
  }

  const closeBoard = () => {
    setShowBoard(false)
    // Ник могли завести или сменить в таблице.
    setPlayerState(getPlayer())
  }

  const toggleSound = () => {
    const next = !muted
    setMutedState(next)
    setMuted(next)
    feedback.setMuted(next)
    if (!next) feedback.unlock()
  }

  return (
    <div className="game">
      <canvas ref={canvasRef} className="game__canvas" aria-label="Игровое поле" />

      {/* Кошелёк — постоянный виджет в углу на любом экране, кроме таблицы
          рекордов (там своя шапка). В игре — живая сумма с учётом монет
          партии, которые физически попадут в кошелёк только на game over. */}
      {!showBoard && (
        <div
          className="game__pill game__pill--plain game__wallet"
          role="status"
          aria-label={`В кошельке: ${wallet + (phase === 'playing' ? gameCoins : 0)}`}
        >
          <IconWallet size={16} />
          {wallet + (phase === 'playing' ? gameCoins : 0)}
        </div>
      )}

      {phase === 'playing' && (
        <div className="game__hud" aria-live="polite">
          <div ref={scoreRef} className="game__score">
            {score}
          </div>
          {combo >= COMBO_BADGE_FROM && (
            <div className="game__pill game__pill--plain game__pill--wide">
              Серия {combo}
              {points > 1 && <span className="game__bonus"> · +{points} за удар</span>}
            </div>
          )}
          {perfectKey > 0 && (
            <div key={perfectKey} className="game__perfect">
              Точно!
            </div>
          )}
          {chips.length > 0 && (
            <div className="game__effects">
              {chips.map((c) => {
                const seconds = effectSeconds(c.kind)
                // Секунды в тексте — как «×2 очков — 5 с» в референсе, а не
                // только полоска: число реально отсчитывает, а не намекает.
                const remaining =
                  c.expiresAt !== null ? Math.max(0, Math.ceil((c.expiresAt - now) / 1000)) : null
                const negative = isNegative(c.kind)
                const highlighted = c.kind === 'double' || c.kind === 'shield'
                return (
                  <div
                    key={c.key}
                    className={[
                      'game__pill',
                      'game__pill--timed',
                      c.kind === 'double' && 'game__pill--gold',
                      c.kind === 'shield' && 'game__pill--info game__pill--cut',
                      !highlighted && negative && 'game__pill--bad',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    data-kind={c.kind}
                  >
                    {highlighted && (
                      <span className="game__pill-icon" aria-hidden="true">
                        {c.kind === 'double' ? '×2' : <IconShield size={16} />}
                      </span>
                    )}
                    {EFFECT_LABEL[c.kind]}
                    {remaining !== null && ` — ${remaining} с`}
                    {seconds !== null && (
                      <span
                        className="game__effect-timer"
                        style={{ animationDuration: `${seconds}s` }}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {phase !== 'playing' && showBoard && (
        <div className="game__overlay">
          <Leaderboard onClose={closeBoard} />
        </div>
      )}

      {phase !== 'playing' && !showBoard && (
        <div className="game__overlay">
          <div className="game__top-bar">
            <button
              type="button"
              className="game__icon-btn game__sound"
              onClick={toggleSound}
              aria-pressed={!muted}
              aria-label={muted ? 'Звук выкл' : 'Звук вкл'}
              data-off={muted}
            >
              {muted ? <IconSoundOff /> : <IconSoundOn />}
            </button>
            {best > 0 && <p className="game__header-best">Лучший счёт: {best}</p>}
          </div>

          {phase === 'over' ? (
            <>
              <p className="game__label">Счёт</p>
              <h1 className="game__title" data-record={isNewBest}>
                {score}
              </h1>
              {isNewBest ? (
                <p className="game__record">Новый рекорд!</p>
              ) : (
                <p className="game__subtitle">Рекорд: {best}</p>
              )}
            </>
          ) : (
            <>
              <h1 className="game__logo" aria-label="Волан">
                <Logo />
              </h1>
              <p className="game__subtitle">
                Води ракетку пальцем и не дай волану упасть. Бей центром — серия даёт бонус
              </p>
            </>
          )}
          {phase === 'over' && gameCoins > 0 && (
            <p className="game__subtitle">
              +{gameCoins} {plural(gameCoins, 'монета', 'монеты', 'монет')}
            </p>
          )}
          {phase === 'over' && !player && (
            <NicknameForm
              label="Ник для таблицы рекордов"
              submitLabel="Сохранить"
              onSubmit={saveNickname}
            />
          )}
          {features.skins &&
            unlocked.map((s) => (
              <p key={s.id} className="game__tag game__tag--notice">
                Открыт скин «{s.name}»
              </p>
            ))}

          {features.skins && (
            <fieldset className="game__skins">
              <legend className="game__skins-title">Скин</legend>
              {skins.map((s) => {
                const open = isUnlocked(s, best)
                return (
                  <button
                    key={s.id}
                    type="button"
                    className="game__skin"
                    aria-pressed={s.id === skin.id}
                    disabled={!open}
                    onClick={() => chooseSkin(s)}
                    title={open ? s.name : `Откроется с рекорда ${s.unlockAt}`}
                  >
                    <span
                      className="game__swatch"
                      style={{ background: s.colors.frame, borderColor: s.colors.corkBand }}
                      aria-hidden="true"
                    />
                    {open ? s.name : `с ${s.unlockAt}`}
                  </button>
                )
              })}
            </fieldset>
          )}

          <button
            type="button"
            className="game__button"
            onClick={start}
            disabled={!canRestart}
            // biome-ignore lint/a11y/noAutofocus: единственное действие на экране — Enter/пробел сразу запускают игру
            autoFocus
          >
            {phase === 'over' ? 'Ещё раз' : 'Играть'}
          </button>
          <button type="button" className="game__secondary" onClick={() => setShowBoard(true)}>
            <IconTrophy />
            Рекорды
          </button>
        </div>
      )}
    </div>
  )
}

/** Русское множественное число: 1 монета, 2 монеты, 5 монет. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}
